import { useCallback, useEffect } from 'react';
import { api, ApiRequestError } from '@/lib/api.js';
import { usePresetStore } from '@/stores/presetStore.js';
import { useParamsStore } from '@/stores/paramsStore.js';
import { useViewStore } from '@/stores/viewStore.js';
import type { NoiseParams } from '@wg/shared';

export function usePresets() {
  const { presets, loading, error, set, setLoading, setError } = usePresetStore();
  const applyParams = useParamsStore((s) => s.setAll);
  const setPalette = useViewStore((s) => s.setPalette);

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try { set(await api.presets.list()); }
    catch (e) { setError(e instanceof ApiRequestError ? e.message : 'Failed to load presets'); }
    finally { setLoading(false); }
  }, [set, setLoading, setError]);

  const save = useCallback(async (name: string) => {
    const params = useParamsStore.getState().params;
    const paletteId = useViewStore.getState().paletteId;
    setError(null);
    try {
      await api.presets.create({ name, params, paletteId });
      await refresh();
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : 'Save failed');
    }
  }, [refresh, setError]);

  const load = useCallback((presetId: string) => {
    const preset = presets.find((p) => p.id === presetId);
    if (!preset) return;
    try {
      const params = JSON.parse(preset.paramsJson) as NoiseParams;
      applyParams(params);
      setPalette(preset.paletteId);
    } catch {
      setError('Preset data is corrupt');
    }
  }, [presets, applyParams, setPalette, setError]);

  const remove = useCallback(async (presetId: string) => {
    try {
      setError(null);
      await api.presets.remove(presetId);
      await refresh();
    } catch (e) { setError(e instanceof ApiRequestError ? e.message : 'Delete failed'); }
  }, [refresh, setError]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const list = await api.presets.list();
        if (!cancelled) set(list);
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiRequestError ? e.message : 'Failed to load presets');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [set, setLoading, setError]);

  return { presets, loading, error, save, load, remove, refresh };
}
